import FormattedDateWithAMPMTimeRenderer from '../../../../core/table/renderers/FormattedDateWithAMPMTimeRenderer';
import { tblSpecAddColumn } from '../../../../core/table/spec';
import { TblCol, TblFltOption } from '../../../../core/table/table.types';
import { TblViewSpec } from '../../../../core/table/table-view.types';
import { feedTypeOptionsForTable } from '../../utils/feedTypeOptions';
import MultipleItemsRenderer from '../../../../core/table/renderers/MultipleItemsRenderer';
import { getUiModeForFeeds } from '../pagehooks/useFeedsInit';
import { getDefaultStore } from 'jotai';
import { feedsUserContextAtom } from '../feeds_list_atoms';

export type ColKey =
    | ''
    | 'FeedType'
    | 'DataSourceParter'
    | 'Direction'
    | 'ImportDate'
    | 'CoveredEntity'
    | 'Pharmacy'
    | 'RecordsReceived'
    | 'RecordsProcessed'
    | 'RecordsDuplicate'
    | 'RecordsRejected'
    | 'RecordsError'
    | 'FeedName'
    | 'ShortFeedName'
    | 'VendorAccount'
    | 'ObjectState';

function typeOptions(): TblFltOption[] {
    // Use the extracted function with VERITY mode for comprehensive feed type list
    return feedTypeOptionsForTable(getUiModeForFeeds());
}

export function statusOptions(): TblFltOption[] {
    return [
        { lbl: 'ARCHIVED', val: 'ARCHIVED' },
        { lbl: 'DUPLICATE', val: 'DUPLICATE' },
        { lbl: 'ERROR', val: 'ERROR' },
        { lbl: 'IN PROGRESS', val: 'INPROGRESS' },
        { lbl: 'PROCESSED', val: 'PROCESSED' },
        { lbl: 'RAW', val: 'RAW' },
        { lbl: 'READY', val: 'READY' },
        { lbl: 'RECEIVED', val: 'RECEIVED' },
        { lbl: 'REJECTED', val: 'REJECTED' },
        { lbl: 'REVERSED', val: 'REVERSED' },
        { lbl: 'SKIPPED', val: 'SKIPPED' },
        { lbl: 'TRANSFERRING', val: 'TRANSFERRING' },
        { lbl: 'WARNING', val: 'WARNING' },
    ];
}

// column spec (map the keys to the column types)
export function feedsListColumnSpec(): Map<ColKey, TblCol> {
    // Note: Confirmed this isn't re-firing on form/table change.
    const { userType } = getDefaultStore().get(feedsUserContextAtom);
    const isCE = userType === 'CE';
    const map = new Map<ColKey, TblCol>();

    // Type section
    tblSpecAddColumn<ColKey>(map, 'FeedType', {
        type: 'MULTISELECT',
        width: '80px',
        title: 'Type',
        fltOptions: typeOptions(),
        required: true,
    });

    // Feed Info section
    tblSpecAddColumn<ColKey>(map, 'DataSourceParter', { width: '80px', title: 'Source' });
    tblSpecAddColumn<ColKey>(map, 'Direction', {
        width: '60px',
        title: 'Dir',
    });
    tblSpecAddColumn<ColKey>(
        map,
        'ImportDate',
        {
            type: 'DATE',
            width: '120px',
            title: 'Import Date',
        },
        FormattedDateWithAMPMTimeRenderer,
    );
    tblSpecAddColumn<ColKey>(
        map,
        'CoveredEntity',
        {
            width: '100px',
            title: 'CEID',
        },
        MultipleItemsRenderer,
    );
    tblSpecAddColumn<ColKey>(
        map,
        'Pharmacy',
        {
            width: '100px',
            title: 'PHID',
        },
        MultipleItemsRenderer,
    );

    // Records Parsing Totals section
    tblSpecAddColumn<ColKey>(map, 'RecordsReceived', {
        type: 'COMMA_SEPARATED_NUMBER',
        width: '60px',
        title: 'Received',
    });
    tblSpecAddColumn<ColKey>(map, 'RecordsProcessed', {
        type: 'COMMA_SEPARATED_NUMBER',
        width: '70px',
        title: 'Processed',
    });
    tblSpecAddColumn<ColKey>(map, 'RecordsDuplicate', {
        type: 'COMMA_SEPARATED_NUMBER',
        width: '70px',
        title: 'Duplicate(s)',
    });
    tblSpecAddColumn<ColKey>(map, 'RecordsRejected', {
        type: 'COMMA_SEPARATED_NUMBER',
        width: '60px',
        title: 'Rejected',
    });
    tblSpecAddColumn<ColKey>(map, 'RecordsError', {
        type: 'COMMA_SEPARATED_NUMBER',
        width: '60px',
        title: 'Error(s)',
    });

    // Details section
    tblSpecAddColumn<ColKey>(map, isCE ? 'ShortFeedName' : 'FeedName', {
        width: '180px',
        title: 'File Name',
    });
    tblSpecAddColumn<ColKey>(map, 'VendorAccount', {
        width: '100px',
        title: 'Vendor Account',
    });

    // Status section
    tblSpecAddColumn<ColKey>(map, 'ObjectState', {
        type: 'MULTISELECT',
        title: 'Status',
        width: '80px',
        fltOptions: statusOptions(),
        required: true,
    });

    return map;
}

// source of truth for all possible columns/sections
export function feedsListAllColumnsView(): TblViewSpec {
    const { userType } = getDefaultStore().get(feedsUserContextAtom);
    const isCE = userType === 'CE';
    return {
        key: '1',
        title: 'All',
        sections: [
            {
                title: 'Type',
                cols: ['FeedType'],
            },
            {
                title: 'Feed Info',
                cols: ['DataSourceParter', 'Direction', 'ImportDate', 'CoveredEntity', 'Pharmacy'],
            },
            {
                title: 'Records Parsing Totals',
                cols: ['RecordsReceived', 'RecordsProcessed', 'RecordsDuplicate', 'RecordsRejected', 'RecordsError'],
            },
            {
                title: 'Details',
                cols: [isCE ? 'ShortFeedName' : 'FeedName', 'VendorAccount'],
            },
            {
                title: 'Status',
                cols: ['ObjectState'],
            },
        ],
        sortby: '-ImportDate',
    };
}
